import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { professionalsAPI, bookingsAPI, categoriesAPI, subscriptionAPI } from '../lib/api';
import { formatDateTime, formatPrice } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Calendar, Clock, MapPin, User, DollarSign, Star, Settings, FileText, CheckCircle, XCircle, CreditCard, AlertTriangle, Zap, Gift, Copy, Users, History, RefreshCw, Phone, Share2, Navigation } from 'lucide-react';

const ProDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, isProfessional, loading: authLoading } = useAuth();
  const { t, language } = useLanguage();
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [categories, setCategories] = useState({});
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState({});
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [referralInfo, setReferralInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [hasProfile, setHasProfile] = useState(true);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const [profileForm, setProfileForm] = useState({
    category_key: '',
    trade: '',
    description: '',
    hourly_rate: '',
    city: 'Gatineau',
    address: '',
    experience_years: '',
    available: true,
  });

  useEffect(() => {
    if (!authLoading) {
      if (!isAuthenticated) {
        navigate('/login');
        return;
      }
      if (!isProfessional) {
        navigate('/dashboard');
        return;
      }
    }
  }, [isAuthenticated, isProfessional, authLoading, navigate]);

  useEffect(() => {
    const subscriptionStatus = searchParams.get('subscription');
    const sessionId = searchParams.get('session_id');

    if (subscriptionStatus === 'success' && sessionId) {
      pollPaymentStatus(sessionId);
    } else if (subscriptionStatus === 'cancelled') {
      toast.info(language === 'fr' ? 'Paiement annulé' : 'Payment cancelled');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      toast.error(language === 'fr' ? 'Vérification du paiement expirée' : 'Payment verification timed out');
      return;
    }

    try {
      const response = await subscriptionAPI.getCheckoutStatus(sessionId);
      
      if (response.data.payment_status === 'paid') {
        toast.success(language === 'fr' ? 'Abonnement activé avec succès!' : 'Subscription activated successfully!');
        fetchSubscription();
        fetchPaymentHistory();
        navigate('/pro/dashboard', { replace: true });
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const fetchSubscription = async () => {
    try {
      const [statusRes, plansRes] = await Promise.all([
        subscriptionAPI.getStatus(),
        subscriptionAPI.getPlans()
      ]);
      setSubscription(statusRes.data);
      setPlans(plansRes.data);
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const fetchPaymentHistory = async () => {
    try {
      const response = await subscriptionAPI.getPaymentHistory();
      setPaymentHistory(response.data);
    } catch (error) {
      console.error('Error fetching payment history:', error);
    }
  };

  const fetchReferralInfo = async () => {
    try {
      const response = await subscriptionAPI.getReferralInfo();
      setReferralInfo(response.data);
    } catch (error) {
      console.error('Error fetching referral info:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes] = await Promise.all([
          categoriesAPI.getAll()
        ]);
        setCategories(catRes.data);

        await Promise.all([
          fetchSubscription(),
          fetchPaymentHistory(),
          fetchReferralInfo()
        ]);

        try {
          const [profileRes, bookingsRes] = await Promise.all([
            professionalsAPI.getMyProfile(),
            bookingsAPI.getAll()
          ]);
          setProfile(profileRes.data);
          setBookings(bookingsRes.data);
          if (profileRes.data.subscription) {
            setSubscription(profileRes.data.subscription);
          }
          setProfileForm({
            category_key: profileRes.data.category_key,
            trade: profileRes.data.trade,
            description: profileRes.data.description,
            hourly_rate: profileRes.data.hourly_rate.toString(),
            city: profileRes.data.city,
            address: profileRes.data.address || '',
            experience_years: profileRes.data.experience_years.toString(),
            available: profileRes.data.available,
          });
        } catch (error) {
          if (error.response?.status === 404) {
            setHasProfile(false);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    if (isProfessional) {
      fetchData();
    }
  }, [isProfessional]);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileLoading(true);

    try {
      const data = {
        ...profileForm,
        hourly_rate: parseFloat(profileForm.hourly_rate),
        experience_years: parseInt(profileForm.experience_years),
      };

      if (hasProfile) {
        const response = await professionalsAPI.updateMyProfile(data);
        setProfile(response.data);
        toast.success(language === 'fr' ? 'Profil mis à jour' : 'Profile updated');
      } else {
        const response = await professionalsAPI.createProfile(data);
        setProfile(response.data);
        setHasProfile(true);
        toast.success(language === 'fr' ? 'Profil créé avec succès' : 'Profile created successfully');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleBookingAction = async (bookingId, status) => {
    try {
      await bookingsAPI.updateStatus(bookingId, status);
      setBookings(bookings.map(b => 
        b.id === bookingId ? { ...b, status } : b
      ));
      const messages = {
        accepted: language === 'fr' ? 'Réservation acceptée' : 'Booking accepted',
        rejected: language === 'fr' ? 'Réservation refusée' : 'Booking rejected',
        completed: language === 'fr' ? 'Réservation terminée' : 'Booking completed'
      };
      toast.success(messages[status]);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const toggleAvailability = async () => {
    const newAvailable = !profileForm.available;
    setProfileForm({ ...profileForm, available: newAvailable });
    
    try {
      await professionalsAPI.updateMyProfile({ available: newAvailable });
      setProfile({ ...profile, available: newAvailable });
      toast.success(newAvailable 
        ? (language === 'fr' ? 'Vous êtes maintenant disponible' : 'You are now available')
        : (language === 'fr' ? 'Vous êtes maintenant indisponible' : 'You are now unavailable')
      );
    } catch (error) {
      setProfileForm({ ...profileForm, available: !newAvailable });
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const toggleAutoRenew = async () => {
    if (!subscription) return;
    
    const newAutoRenew = !subscription.auto_renew;
    
    try {
      await subscriptionAPI.updateAutoRenew(newAutoRenew);
      setSubscription({ ...subscription, auto_renew: newAutoRenew });
      toast.success(newAutoRenew 
        ? (language === 'fr' ? 'Renouvellement automatique activé' : 'Auto-renewal enabled')
        : (language === 'fr' ? 'Renouvellement automatique désactivé' : 'Auto-renewal disabled')
      );
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleSubscribe = async (planId) => {
    setCheckoutLoading(true);
    try {
      const response = await subscriptionAPI.createCheckout({
        plan_id: planId,
        origin_url: window.location.origin
      });
      
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du paiement');
      setCheckoutLoading(false);
    }
  };

  const copyReferralLink = () => {
    if (referralInfo?.referral_link) {
      navigator.clipboard.writeText(referralInfo.referral_link);
      toast.success(language === 'fr' ? 'Lien copié!' : 'Link copied!');
    }
  };

  const shareReferralLink = async () => {
    if (!referralInfo?.referral_link) return;
    
    const shareData = {
      title: 'HandyVerse Pro',
      text: language === 'fr' 
        ? 'Rejoignez HandyVerse et développez votre activité! Utilisez mon lien de parrainage pour un mois gratuit.'
        : 'Join HandyVerse and grow your business! Use my referral link for a free month.',
      url: referralInfo.referral_link
    };

    try {
      if (navigator.share && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        toast.success(language === 'fr' ? 'Partagé!' : 'Shared!');
      } else {
        // Fallback to copy
        copyReferralLink();
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        copyReferralLink();
      }
    }
  };

  const availableTrades = profileForm.category_key && categories[profileForm.category_key]
    ? categories[profileForm.category_key].trades
    : [];

  const pendingBookings = bookings.filter(b => b.status === 'pending');
  const activeBookings = bookings.filter(b => b.status === 'accepted');

  const getSubscriptionStatusBadge = () => {
    if (!subscription) return null;
    
    const badges = {
      trial: { label: language === 'fr' ? 'Essai gratuit' : 'Free trial', class: 'bg-blue-100 text-blue-700' },
      active: { label: language === 'fr' ? 'Actif' : 'Active', class: 'bg-green-100 text-green-700' },
      expired: { label: language === 'fr' ? 'Expiré' : 'Expired', class: 'bg-red-100 text-red-700' },
      none: { label: language === 'fr' ? 'Aucun' : 'None', class: 'bg-gray-100 text-gray-700' }
    };
    
    const badge = badges[subscription.status] || badges.none;
    return <Badge className={badge.class}>{badge.label}</Badge>;
  };

  if (authLoading || !isProfessional) {
    return <div className="min-h-screen flex items-center justify-center">Chargement...</div>;
  }

  return (
    <div className="min-h-screen py-8" data-testid="pro-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl text-slate-900 mb-1" style={{ fontFamily: '"DM Serif Display", serif' }}>
              {t('dashboardTitle')}
            </h1>
            <p className="text-slate-600">{t('manageProfileBookings')}</p>
          </div>
          {hasProfile && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-600">{t('availability')}</span>
              <Switch
                checked={profileForm.available}
                onCheckedChange={toggleAvailability}
                data-testid="availability-toggle"
              />
              <span className={`text-sm font-medium ${profileForm.available ? 'text-green-600' : 'text-red-600'}`}>
                {profileForm.available ? t('available') : t('unavailable')}
              </span>
            </div>
          )}
        </div>

        {/* Subscription Alert */}
        {subscription && !subscription.can_receive_bookings && (
          <Card className="border-orange-200 bg-orange-50 mb-6">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="h-6 w-6 text-orange-600 shrink-0" />
                <div className="flex-1">
                  <h3 className="font-semibold text-orange-900 mb-1">
                    {language === 'fr' ? 'Limite de réservations atteinte' : 'Booking limit reached'}
                  </h3>
                  <p className="text-orange-700 text-sm mb-3">
                    {language === 'fr' 
                      ? `Vous avez utilisé vos ${subscription.bookings_limit} réservations gratuites ce mois-ci. Abonnez-vous pour recevoir des réservations illimitées.`
                      : `You have used your ${subscription.bookings_limit} free bookings this month. Subscribe to receive unlimited bookings.`
                    }
                  </p>
                  <Button 
                    onClick={() => setShowSubscriptionModal(true)}
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                    data-testid="subscribe-alert-btn"
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    {language === 'fr' ? "S'abonner maintenant" : 'Subscribe now'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Referral Bonus Banner */}
        {referralInfo && referralInfo.bonus_days_earned > 0 && (
          <Card className="border-green-200 bg-green-50 mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <Gift className="h-8 w-8 text-green-600" />
                <div>
                  <h3 className="font-semibold text-green-900">
                    {language === 'fr' ? `+${referralInfo.bonus_days_earned} jours bonus gagnés!` : `+${referralInfo.bonus_days_earned} bonus days earned!`}
                  </h3>
                  <p className="text-green-700 text-sm">
                    {language === 'fr' 
                      ? `Merci d'avoir parrainé ${referralInfo.successful_referrals} professionnel(s)`
                      : `Thanks for referring ${referralInfo.successful_referrals} professional(s)`
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!hasProfile ? (
          // Profile Creation Form
          <Card className="border-slate-100 max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle style={{ fontFamily: '"DM Serif Display", serif' }}>
                {t('createProProfileTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleProfileSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('category')}</Label>
                    <Select
                      value={profileForm.category_key}
                      onValueChange={(value) => setProfileForm({ ...profileForm, category_key: value, trade: '' })}
                    >
                      <SelectTrigger className="h-12" data-testid="profile-category-select">
                        <SelectValue placeholder={t('allCategories')} />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(categories).map(([key, cat]) => (
                          <SelectItem key={key} value={key}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>{t('trade')}</Label>
                    <Select
                      value={profileForm.trade}
                      onValueChange={(value) => setProfileForm({ ...profileForm, trade: value })}
                      disabled={!profileForm.category_key}
                    >
                      <SelectTrigger className="h-12" data-testid="profile-trade-select">
                        <SelectValue placeholder={t('allTrades')} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableTrades.map((trade) => (
                          <SelectItem key={trade} value={trade}>{trade}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('description')}</Label>
                  <Textarea
                    placeholder={t('describeActivity')}
                    value={profileForm.description}
                    onChange={(e) => setProfileForm({ ...profileForm, description: e.target.value })}
                    rows={4}
                    className="resize-none"
                    required
                    data-testid="profile-description-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>{t('hourlyRate')}</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="35"
                      value={profileForm.hourly_rate}
                      onChange={(e) => setProfileForm({ ...profileForm, hourly_rate: e.target.value })}
                      className="h-12"
                      required
                      data-testid="profile-rate-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('yearsExperience')}</Label>
                    <Input
                      type="number"
                      min="0"
                      placeholder="5"
                      value={profileForm.experience_years}
                      onChange={(e) => setProfileForm({ ...profileForm, experience_years: e.target.value })}
                      className="h-12"
                      required
                      data-testid="profile-experience-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('city')}</Label>
                    <Input
                      placeholder="Gatineau"
                      value={profileForm.city}
                      onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
                      className="h-12"
                      required
                      data-testid="profile-city-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('address')}</Label>
                  <Input
                    placeholder="123 rue Principale"
                    value={profileForm.address}
                    onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
                    className="h-12"
                    data-testid="profile-address-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white rounded-full btn-press"
                  disabled={profileLoading}
                  data-testid="profile-submit-btn"
                >
                  {profileLoading ? 'Création...' : t('createMyProfile')}
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              <Card className="border-slate-100">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-yellow-50 flex items-center justify-center">
                      <Clock className="h-5 w-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{pendingBookings.length}</p>
                      <p className="text-xs text-slate-600">{t('pending')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-100">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                      <Calendar className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{activeBookings.length}</p>
                      <p className="text-xs text-slate-600">{t('accepted')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-100">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                      <Star className="h-5 w-5 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">
                        {profile?.average_rating > 0 ? profile.average_rating.toFixed(1) : '-'}
                      </p>
                      <p className="text-xs text-slate-600">{t('rating')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-100">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                      <CreditCard className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      {getSubscriptionStatusBadge()}
                      <p className="text-xs text-slate-600 mt-1">
                        {subscription?.bookings_this_month || 0}/{subscription?.bookings_limit || 5}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-100">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-pink-50 flex items-center justify-center">
                      <Gift className="h-5 w-5 text-pink-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{referralInfo?.successful_referrals || 0}</p>
                      <p className="text-xs text-slate-600">{language === 'fr' ? 'Parrainages' : 'Referrals'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Booking Progress */}
            {subscription && subscription.status !== 'active' && (
              <Card className="border-slate-100 mb-6">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700">
                      {language === 'fr' ? 'Réservations ce mois-ci' : 'Bookings this month'}
                    </span>
                    <span className="text-sm text-slate-500">
                      {subscription.bookings_this_month}/{subscription.bookings_limit}
                    </span>
                  </div>
                  <Progress 
                    value={(subscription.bookings_this_month / subscription.bookings_limit) * 100} 
                    className="h-2"
                  />
                  {subscription.bookings_this_month >= subscription.bookings_limit && (
                    <Button 
                      onClick={() => setShowSubscriptionModal(true)}
                      variant="link" 
                      className="text-orange-600 p-0 h-auto mt-2"
                    >
                      <Zap className="h-4 w-4 mr-1" />
                      {language === 'fr' ? 'Passer illimité' : 'Go unlimited'}
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Tabs */}
            <Tabs defaultValue="requests" className="space-y-6">
              <TabsList className="bg-slate-100 p-1 rounded-lg flex-wrap h-auto">
                <TabsTrigger value="requests" className="rounded-md" data-testid="tab-requests">
                  {t('requests')} ({pendingBookings.length})
                </TabsTrigger>
                <TabsTrigger value="bookings" className="rounded-md" data-testid="tab-bookings">
                  {t('bookings')} ({activeBookings.length})
                </TabsTrigger>
                <TabsTrigger value="subscription" className="rounded-md" data-testid="tab-subscription">
                  {language === 'fr' ? 'Abonnement' : 'Subscription'}
                </TabsTrigger>
                <TabsTrigger value="referral" className="rounded-md" data-testid="tab-referral">
                  {language === 'fr' ? 'Parrainage' : 'Referral'}
                </TabsTrigger>
                <TabsTrigger value="profile" className="rounded-md" data-testid="tab-profile">
                  {t('myProfile')}
                </TabsTrigger>
              </TabsList>

              {/* Pending Requests */}
              <TabsContent value="requests">
                <Card className="border-slate-100">
                  <CardHeader>
                    <CardTitle style={{ fontFamily: '"DM Serif Display", serif' }}>
                      {t('pendingRequests')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {pendingBookings.length === 0 ? (
                      <div className="text-center py-12">
                        <Clock className="h-12 w-12 mx-auto text-slate-200 mb-4" />
                        <p className="text-slate-600">{t('noPendingRequests')}</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {pendingBookings.map((booking) => (
                          <div
                            key={booking.id}
                            className="p-4 rounded-xl bg-yellow-50 border border-yellow-100"
                            data-testid={`request-card-${booking.id}`}
                          >
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <User className="h-5 w-5 text-slate-600" />
                                  <h3 className="font-semibold text-slate-900">{booking.client_name}</h3>
                                </div>
                                <p className="text-sm text-slate-600 mb-2">{booking.description}</p>
                                <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="h-4 w-4" />
                                    {formatDateTime(booking.scheduled_date)}
                                  </span>
                                  <a 
                                    href={`https://maps.google.com/?q=${encodeURIComponent(booking.address)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1 text-blue-600 hover:text-blue-700"
                                  >
                                    <Navigation className="h-4 w-4" />
                                    {booking.address}
                                  </a>
                                </div>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  onClick={() => handleBookingAction(booking.id, 'accepted')}
                                  className="bg-green-600 hover:bg-green-700 text-white rounded-full"
                                  size="sm"
                                  data-testid={`accept-${booking.id}`}
                                >
                                  <CheckCircle className="h-4 w-4 mr-1" />
                                  {t('accept')}
                                </Button>
                                <Button
                                  onClick={() => handleBookingAction(booking.id, 'rejected')}
                                  variant="outline"
                                  className="border-red-200 text-red-600 hover:bg-red-50 rounded-full"
                                  size="sm"
                                  data-testid={`reject-${booking.id}`}
                                >
                                  <XCircle className="h-4 w-4 mr-1" />
                                  {t('reject')}
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Active Bookings */}
              <TabsContent value="bookings">
                <Card className="border-slate-100">
                  <CardHeader>
                    <CardTitle style={{ fontFamily: '"DM Serif Display", serif' }}>
                      {t('acceptedBookings')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {activeBookings.length === 0 ? (
                      <div className="text-center py-12">
                        <Calendar className="h-12 w-12 mx-auto text-slate-200 mb-4" />
                        <p className="text-slate-600">{t('noActiveBookings')}</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {activeBookings.map((booking) => (
                          <div
                            key={booking.id}
                            className="p-4 rounded-xl bg-green-50 border border-green-100"
                            data-testid={`booking-card-${booking.id}`}
                          >
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-2">
                                  <User className="h-5 w-5 text-slate-600" />
                                  <h3 className="font-semibold text-slate-900">{booking.client_name}</h3>
                                  <Badge className="status-accepted">{t('statusAccepted')}</Badge>
                                </div>
                                <p className="text-sm text-slate-600 mb-2">{booking.description}</p>
                                <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="h-4 w-4" />
                                    {formatDateTime(booking.scheduled_date)}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <MapPin className="h-4 w-4" />
                                    {booking.address}
                                  </span>
                                </div>
                              </div>
                              <Button
                                onClick={() => handleBookingAction(booking.id, 'completed')}
                                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full"
                                size="sm"
                                data-testid={`complete-${booking.id}`}
                              >
                                {t('markCompleted')}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Subscription Tab */}
              <TabsContent value="subscription">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Current Status */}
                  <Card className="border-slate-100">
                    <CardHeader>
                      <CardTitle style={{ fontFamily: '"DM Serif Display", serif' }}>
                        {language === 'fr' ? 'Statut actuel' : 'Current status'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-600">{language === 'fr' ? 'Statut' : 'Status'}</span>
                          {getSubscriptionStatusBadge()}
                        </div>
                        {subscription?.plan && (
                          <div className="flex items-center justify-between">
                            <span className="text-slate-600">{language === 'fr' ? 'Plan' : 'Plan'}</span>
                            <span className="font-medium">
                              {language === 'fr' ? plans[subscription.plan]?.name : plans[subscription.plan]?.name_en}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="text-slate-600">{language === 'fr' ? 'Réservations ce mois' : 'Bookings this month'}</span>
                          <span className="font-medium">{subscription?.bookings_this_month || 0}</span>
                        </div>
                        {subscription?.subscription_ends_at && subscription?.status === 'active' && (
                          <div className="flex items-center justify-between">
                            <span className="text-slate-600">{language === 'fr' ? 'Expire le' : 'Expires'}</span>
                            <span className="font-medium">
                              {new Date(subscription.subscription_ends_at).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                        {subscription?.referral_bonus_days > 0 && (
                          <div className="flex items-center justify-between">
                            <span className="text-slate-600 flex items-center gap-1">
                              <Gift className="h-4 w-4" />
                              {language === 'fr' ? 'Bonus parrainage' : 'Referral bonus'}
                            </span>
                            <span className="font-medium text-green-600">
                              +{subscription.referral_bonus_days} {language === 'fr' ? 'jours' : 'days'}
                            </span>
                          </div>
                        )}
                        
                        {/* Auto-renew toggle */}
                        {subscription?.status === 'active' && (
                          <div className="pt-4 border-t border-slate-100">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <RefreshCw className="h-4 w-4 text-slate-500" />
                                <span className="text-sm text-slate-600">
                                  {language === 'fr' ? 'Renouvellement auto' : 'Auto-renewal'}
                                </span>
                              </div>
                              <Switch
                                checked={subscription.auto_renew}
                                onCheckedChange={toggleAutoRenew}
                                data-testid="auto-renew-toggle"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Subscription Plans */}
                  <Card className="border-slate-100">
                    <CardHeader>
                      <CardTitle style={{ fontFamily: '"DM Serif Display", serif' }}>
                        {language === 'fr' ? 'Plans disponibles' : 'Available plans'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {Object.entries(plans).map(([key, plan]) => (
                        <div 
                          key={key}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            key === 'yearly' ? 'border-orange-300 bg-orange-50' : 'border-slate-200'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <h4 className="font-semibold text-slate-900">
                                {language === 'fr' ? plan.name : plan.name_en}
                              </h4>
                              {plan.savings && (
                                <Badge className="bg-orange-600 text-white mt-1">
                                  {language === 'fr' ? `Économisez ${plan.savings}` : `Save ${plan.savings}`}
                                </Badge>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-bold text-slate-900">
                                {formatPrice(plan.price)}
                              </p>
                              <p className="text-xs text-slate-500">
                                /{plan.interval === 'month' 
                                  ? (language === 'fr' ? 'mois' : 'month')
                                  : (language === 'fr' ? 'an' : 'year')
                                }
                              </p>
                            </div>
                          </div>
                          <Button
                            onClick={() => handleSubscribe(key)}
                            className={`w-full mt-2 rounded-full text-sm ${
                              key === 'yearly' 
                                ? 'bg-orange-600 hover:bg-orange-700 text-white'
                                : 'bg-slate-900 hover:bg-slate-800 text-white'
                            }`}
                            disabled={checkoutLoading || subscription?.status === 'active'}
                            data-testid={`subscribe-${key}-btn`}
                          >
                            {checkoutLoading ? '...' : (
                              subscription?.status === 'active' 
                                ? (language === 'fr' ? 'Déjà abonné' : 'Already subscribed')
                                : (language === 'fr' ? "S'abonner" : 'Subscribe')
                            )}
                          </Button>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  {/* Payment History */}
                  <Card className="border-slate-100">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2" style={{ fontFamily: '"DM Serif Display", serif' }}>
                        <History className="h-5 w-5" />
                        {language === 'fr' ? 'Historique des paiements' : 'Payment history'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {paymentHistory.length === 0 ? (
                        <div className="text-center py-8">
                          <History className="h-10 w-10 mx-auto text-slate-200 mb-3" />
                          <p className="text-slate-500 text-sm">
                            {language === 'fr' ? 'Aucun paiement' : 'No payments'}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {paymentHistory.slice(0, 5).map((payment) => (
                            <div key={payment.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <div>
                                <p className="font-medium text-slate-900 text-sm">{payment.plan_name}</p>
                                <p className="text-xs text-slate-500">
                                  {new Date(payment.created_at).toLocaleDateString()}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="font-semibold text-slate-900">{formatPrice(payment.amount)}</p>
                                <Badge className="bg-green-100 text-green-700 text-xs">
                                  {language === 'fr' ? 'Payé' : 'Paid'}
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Referral Tab */}
              <TabsContent value="referral">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Referral Link */}
                  <Card className="border-slate-100">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2" style={{ fontFamily: '"DM Serif Display", serif' }}>
                        <Gift className="h-5 w-5 text-orange-600" />
                        {language === 'fr' ? 'Programme de parrainage' : 'Referral program'}
                      </CardTitle>
                      <CardDescription>
                        {language === 'fr' 
                          ? 'Gagnez 1 mois gratuit pour chaque professionnel parrainé!'
                          : 'Earn 1 free month for each professional you refer!'
                        }
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="p-4 bg-gradient-to-r from-orange-50 to-pink-50 rounded-xl border border-orange-100">
                        <p className="text-sm text-slate-600 mb-2">
                          {language === 'fr' ? 'Votre lien de parrainage' : 'Your referral link'}
                        </p>
                        <div className="flex gap-2">
                          <Input
                            value={referralInfo?.referral_link || ''}
                            readOnly
                            className="text-sm bg-white"
                          />
                          <Button
                            onClick={copyReferralLink}
                            variant="outline"
                            className="shrink-0"
                            data-testid="copy-referral-link"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button
                            onClick={shareReferralLink}
                            className="shrink-0 bg-orange-600 hover:bg-orange-700 text-white"
                            data-testid="share-referral-link"
                          >
                            <Share2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-slate-50 rounded-xl text-center">
                          <Users className="h-6 w-6 mx-auto text-slate-400 mb-2" />
                          <p className="text-2xl font-bold text-slate-900">{referralInfo?.successful_referrals || 0}</p>
                          <p className="text-xs text-slate-500">
                            {language === 'fr' ? 'Parrainages réussis' : 'Successful referrals'}
                          </p>
                        </div>
                        <div className="p-4 bg-green-50 rounded-xl text-center">
                          <Gift className="h-6 w-6 mx-auto text-green-500 mb-2" />
                          <p className="text-2xl font-bold text-green-600">+{referralInfo?.bonus_days_earned || 0}</p>
                          <p className="text-xs text-slate-500">
                            {language === 'fr' ? 'Jours gagnés' : 'Days earned'}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Referral History */}
                  <Card className="border-slate-100">
                    <CardHeader>
                      <CardTitle style={{ fontFamily: '"DM Serif Display", serif' }}>
                        {language === 'fr' ? 'Vos parrainages' : 'Your referrals'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {!referralInfo?.referrals || referralInfo.referrals.length === 0 ? (
                        <div className="text-center py-12">
                          <Users className="h-12 w-12 mx-auto text-slate-200 mb-4" />
                          <p className="text-slate-500 mb-2">
                            {language === 'fr' ? 'Aucun parrainage pour le moment' : 'No referrals yet'}
                          </p>
                          <p className="text-sm text-slate-400">
                            {language === 'fr' 
                              ? 'Partagez votre lien pour commencer à gagner!'
                              : 'Share your link to start earning!'
                            }
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {referralInfo.referrals.map((referral, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                                  <User className="h-4 w-4 text-orange-600" />
                                </div>
                                <div>
                                  <p className="font-medium text-slate-900 text-sm">{referral.name}</p>
                                  <p className="text-xs text-slate-500">
                                    {new Date(referral.date).toLocaleDateString()}
                                  </p>
                                </div>
                              </div>
                              <Badge className="bg-green-100 text-green-700">
                                +{referral.bonus_days} {language === 'fr' ? 'jours' : 'days'}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Profile Tab */}
              <TabsContent value="profile">
                <Card className="border-slate-100">
                  <CardHeader>
                    <CardTitle style={{ fontFamily: '"DM Serif Display", serif' }}>
                      <Settings className="h-5 w-5 inline mr-2" />
                      {t('editProfile')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleProfileSubmit} className="space-y-6">
                      <div className="space-y-2">
                        <Label>{t('description')}</Label>
                        <Textarea
                          placeholder={t('describeActivity')}
                          value={profileForm.description}
                          onChange={(e) => setProfileForm({ ...profileForm, description: e.target.value })}
                          rows={4}
                          className="resize-none"
                          required
                          data-testid="edit-description-input"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>{t('hourlyRate')}</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={profileForm.hourly_rate}
                            onChange={(e) => setProfileForm({ ...profileForm, hourly_rate: e.target.value })}
                            className="h-12"
                            required
                            data-testid="edit-rate-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('yearsExperience')}</Label>
                          <Input
                            type="number"
                            min="0"
                            value={profileForm.experience_years}
                            onChange={(e) => setProfileForm({ ...profileForm, experience_years: e.target.value })}
                            className="h-12"
                            required
                            data-testid="edit-experience-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('city')}</Label>
                          <Input
                            value={profileForm.city}
                            onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
                            className="h-12"
                            required
                            data-testid="edit-city-input"
                          />
                        </div>
                      </div>

                      <Button
                        type="submit"
                        className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 h-12 btn-press"
                        disabled={profileLoading}
                        data-testid="save-profile-btn"
                      >
                        {profileLoading ? '...' : t('save')}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>

      {/* Subscription Modal */}
      <Dialog open={showSubscriptionModal} onOpenChange={setShowSubscriptionModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: '"DM Serif Display", serif' }}>
              {language === 'fr' ? 'Choisissez votre abonnement' : 'Choose your subscription'}
            </DialogTitle>
            <DialogDescription>
              {language === 'fr' 
                ? 'Débloquez des réservations illimitées et développez votre activité'
                : 'Unlock unlimited bookings and grow your business'
              }
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {Object.entries(plans).map(([key, plan]) => (
              <div 
                key={key}
                className={`p-4 rounded-xl border-2 transition-all cursor-pointer hover:shadow-md ${
                  key === 'yearly' ? 'border-orange-300 bg-orange-50' : 'border-slate-200 hover:border-slate-300'
                }`}
                onClick={() => handleSubscribe(key)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-slate-900">
                      {language === 'fr' ? plan.name : plan.name_en}
                    </h4>
                    {plan.savings && (
                      <Badge className="bg-orange-600 text-white mt-1">
                        {language === 'fr' ? `Économisez ${plan.savings}` : `Save ${plan.savings}`}
                      </Badge>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-slate-900">
                      {formatPrice(plan.price)}
                    </p>
                    <p className="text-sm text-slate-500">
                      /{plan.interval === 'month' 
                        ? (language === 'fr' ? 'mois' : 'month')
                        : (language === 'fr' ? 'an' : 'year')
                      }
                    </p>
                  </div>
                </div>
              </div>
            ))}
            <p className="text-xs text-center text-slate-500 pt-2">
              {language === 'fr' 
                ? '30 jours d\'essai gratuit inclus • Renouvellement automatique • Annulez à tout moment'
                : '30-day free trial included • Auto-renewal • Cancel anytime'
              }
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProDashboard;
